import React, { useState } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
  createColumnHelper,
} from "@tanstack/react-table";
import { CSVLink } from "react-csv";
import cropr from "../../../shared/utils/cropr";
import gettext from "../../../shared/utils/gettext";
import { RawDataTypes } from "./ObjectInterfaces";

/* Assigning the prop types */
interface ViewRawDataProps {
  getDisplayName: any;
  rawData: RawDataTypes;
}

// Define the column meta type to include width
interface ColumnMeta {
  width?: string;
}

/* ViewRawData Component */
export const ViewRawData = ({ getDisplayName, rawData }: ViewRawDataProps) => {
  const [sorting, setSorting] = useState<any[]>([]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const columnHelper = createColumnHelper<any>();

  /* Iterating over the data to create a column for each measurement */
  const columns = rawData.headers.map((header: string) => {
    if (header === "lat" || header === "lon") {
      return columnHelper.accessor(header, {
        header: () => header,
        cell: (info) => cropr.toLocaleFixed(info.getValue(), 8),
        meta: { width: "150px" } as ColumnMeta,
      });
    } else {
      const headerArr: string[] = header.split("|");
      const headerName: string = getDisplayName(headerArr[1], headerArr[0].split("+")[1]);
      return columnHelper.accessor(headerArr[0], {
        header: () => headerName,
        cell: (info) => cropr.toLocaleFixed(info.getValue(), 2),
        meta: { width: "275px" } as ColumnMeta,
      });
    }
  });

  // Add an ID column if needed
  const tableColumns = [
    columnHelper.accessor("id", {
      header: "ID",
      enableHiding: true, // Hide this column
    }),
    ...columns,
  ];

  const table = useReactTable({
    data: rawData.points || [],
    columns: tableColumns,
    state: {
      sorting,
      pagination,
    },
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  // Prepare CSV data
  const csvHeaders = columns.map((col) => ({
    label: typeof col.header === "function" ? col.header({} as any) : String(col.header),
    key: col.accessorKey || String(col.id),
  }));

  // CSV filename
  const csvFilename = `Raw_Correlation_Data-${new Date().toISOString().slice(0, 10)}.csv`;

  // Helper function to get sort indicator
  const getSortIndicator = (sortDirection: string | boolean) => {
    if (sortDirection === "asc") return " ^";
    if (sortDirection === "desc") return " v";
    return "";
  };

  /* JSX */
  return (
    <div style={{ width: `${275 * (rawData.headers.length - 2) + 300}px` }}>
      <div className="table-responsive">
        <table className="table table-striped table-bordered">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    style={{
                      width: (header.column.columnDef.meta as ColumnMeta | undefined)?.width,
                      cursor: header.column.getCanSort() ? "pointer" : "default",
                    }}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                    {getSortIndicator(header.column.getIsSorted())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="d-flex justify-content-between">
        <div className="d-flex gap-2 align-items-center">
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}
          >
            {"<<"}
          </button>
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            {"<"}
          </button>
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            {">"}
          </button>
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
            disabled={!table.getCanNextPage()}
          >
            {">>"}
          </button>
          <span>
            {gettext("Page")} {table.getState().pagination.pageIndex + 1} {gettext("of")}{" "}
            {table.getPageCount()}
          </span>
        </div>
        <select
          className="form-select form-select-sm w-auto"
          value={table.getState().pagination.pageSize}
          onChange={(e) => {
            table.setPageSize(Number(e.target.value));
          }}
        >
          {[10, 20, 30, 40, 50].map((pageSize) => (
            <option key={pageSize} value={pageSize}>
              {gettext("Show")} {pageSize}
            </option>
          ))}
        </select>
      </div>

      <hr />
      <CSVLink
        data={rawData.points}
        headers={csvHeaders.map((header) => ({
          label: header.label,
          key: String(header.key),
        }))}
        filename={csvFilename}
        className="btn btn-primary btn-lg"
      >
        {gettext("Download CSV")}
      </CSVLink>
    </div>
  );
};
